import { portalText, usePortalTranslation } from "../i18n/portal.js";
/**
 * The notification centre.
 *
 * The bell carries an unread pip rather than a count: the design shows one, and a count on
 * a list this short is noise. "Mark all read" is a mutation, so the list is invalidated
 * rather than mutated in place — the gateway remains the only thing that knows what is read.
 *
 * AMENDED 2026-09-25 (customer-portal review #73): a notice with a `linkPath` (a finished build
 * opens its agent) is a link, so "your agent is ready" leads somewhere; and the empty line sits
 * inside the panel's padding instead of flush against its edge.
 *
 * AMENDED 2026-10-13 (plan-018 WAPP-B): a link may carry a query — a message a caller left opens its conversation in
 * History (`/agents/{id}?tab=history&…`). The router reads `to` as a path only, so the query is handed over as the
 * link's `search`, each value the text it was.
 */
import { Link } from "@tanstack/react-router";
import { Icon } from "@wetalk/ui";
import type { ReactNode } from "react";

import "./notification-centre.css";

import { GLYPH, glyphOf } from "./glyph.js";
import { usePopover } from "./popover.js";
import type { NotificationView } from "../api/gateway.js";

export interface NotificationCentreProps {
  readonly notification: readonly NotificationView[];
  readonly onReadAll: () => void;
}

export function NotificationCentre({
  notification,
  onReadAll,
}: NotificationCentreProps): ReactNode {
  usePortalTranslation("shell");
  const { open, anchorRef, triggerRef, toggle } = usePopover();
  const unreadCount = notification.filter((one) => one.unread).length;
  // The accessible NAME is the prototype's "Notifications"; the unread count is its
  // DESCRIPTION, so a screen reader still hears it without the name changing with the count.
  const unreadText = unreadCount === 0 ? null : portalText("shell", "value_unread_4b9e4b49", { v0: String(String(unreadCount)) });

  return (
    <div className="c-pop-anchor" ref={anchorRef}>
      <button
        type="button"
        ref={triggerRef}
        className="btn btn-icon c-bell"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={portalText("shell", "notifications_753a22b2")}
        aria-describedby={unreadText === null ? undefined : "c-bell-unread"}
        onClick={toggle}
      >
        <Icon glyph={GLYPH.bell} size="lg" />
        {unreadText === null ? null : <span className="c-bell-pip" />}
      </button>
      {unreadText === null ? null : (
        <span id="c-bell-unread" className="c-visually-hidden">
          {unreadText}
        </span>
      )}

      {open ? (
        <div className="c-pop c-pop-wide" role="menu" aria-label={portalText("shell", "notifications_753a22b2")}>
          <div className="c-pop-head">
            <span>{portalText("shell", "notifications_753a22b2")}</span>
            <button type="button" className="btn btn-ghost" onClick={onReadAll}>
              {portalText("shell", "mark_all_read_8958e22c") + " "}</button>
          </div>
          {notification.length === 0 ? (
            <p className="c-note c-pop-empty">
              {portalText("shell", "nothing_yet_builds_alerts_and_flagged_conversations_land_h_4da01962") + " "}</p>
          ) : (
            notification.map((one) => {
              const className = one.unread ? "c-pop-item c-pop-item-unread" : "c-pop-item";
              const body = (
                <>
                  <Icon glyph={glyphOf(one.iconName)} size="lg" tone="accent" />
                  <span className="c-pop-item-body">
                    <span className="c-pop-item-title">{one.title}</span>
                    <span className="c-pop-item-dek">{one.dek}</span>
                    <span className="c-pop-item-dek">{one.whenText}</span>
                  </span>
                </>
              );
              return one.linkPath === null ? (
                <span key={one.notificationId} role="menuitem" tabIndex={0} className={className}>
                  {body}
                </span>
              ) : (
                <Link
                  key={one.notificationId}
                  role="menuitem"
                  {...linkTargetOf(one.linkPath)}
                  className={`${className} c-pop-item-link`}
                  onClick={toggle}
                >
                  {body}
                </Link>
              );
            })
          )}
          {/* The prototype's foot: a rule, then "Which alerts reach me" — the alert settings on the Settings page. */}
          <div className="c-pop-rule" />
          <Link to="/settings" role="menuitem" className="c-pop-item" onClick={toggle}>
            <Icon glyph={GLYPH.slidersHorizontal} size="lg" />
            <span className="c-pop-item-body">{portalText("shell", "notifications.whichAlerts")}</span>
          </Link>
        </div>
      ) : null}
    </div>
  );
}

/** `to` and `search` for a notice's link: the path, and its query (if any) as text values. */
export function linkTargetOf(linkPath: string): { readonly to: string; readonly search?: Readonly<Record<string, string>> } {
  const at = linkPath.indexOf("?");
  if (at < 0) return { to: linkPath };
  return { to: linkPath.slice(0, at), search: Object.fromEntries(new URLSearchParams(linkPath.slice(at + 1))) };
}
